import { AppShell, Box, createStyles } from "@mantine/core";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Outlet } from "react-router-dom";
import { IAuth } from "../../interfaces/IAuth";
import { useScreenDetector } from "../screen-detector";
import { Breadcrumb } from "./breadcrumb";
import { Header } from "./header";
import { Navigation } from "./navbar";

export function Layout() {
  const { isMobile } = useScreenDetector();
  const [opened, setOpened] = useState<boolean>(false);
  const { user } = useSelector((state: { auth: IAuth }) => state.auth);

  return (
    <AppShell
      padding={0}
      navbar={opened || !isMobile ? user && user?.id ? <Navigation opened={opened} setOpened={setOpened} /> : <></> : <></>}
      header={<Header opened={opened} setOpened={setOpened} />}
      styles={(_theme) => ({
        main: { backgroundColor: "transparent" },
      })}>
      <Outlet />
    </AppShell>
  );
}

type PageProps = {
  title: string | JSX.Element;
  subTitle?: string | JSX.Element;
  extra?: React.ReactNode[];
  children: React.ReactNode;
  breadcrumb?: { to?: string; label: string }[];
  bgColor?: string;
};

export function PageLayout({ title, subTitle, breadcrumb, bgColor = "white", extra = [], children }: PageProps) {
  const { classes } = useStyles();

  return (
    <div
      className={classes.pageLayout}
      style={{
        backgroundColor: bgColor,
      }}>
      {breadcrumb && <Breadcrumb data={breadcrumb} />}

      <div className={classes.titleLayout}>
        <div>
          <h2 className={classes.title}>{title}</h2>
          <div className={classes.subTitle}>{subTitle}</div>
        </div>
        <div className={classes.extra}>{extra}</div>
      </div>
      <div className={classes.content}>{children}</div>
    </div>
  );
}

type FormProps = {
  title: string | JSX.Element;
  subTitle?: string | JSX.Element;
  extra?: React.ReactNode[];
  children: React.ReactNode;
  bgColor?: string;
  my?: number | string;
};

export function FormLayout({ title, subTitle, bgColor = "white", extra = [], children, my = 20 }: FormProps) {
  const { classes } = useStyles();

  return (
    <Box
      className={classes.formLayout}
      my={my}
      style={{
        backgroundColor: bgColor,
      }}>
      <div className={classes.titleLayout}>
        <div>
          <h2 className={classes.title}>{title}</h2>
          <p className={classes.subTitle}>{subTitle}</p>
        </div>
        <div className={classes.extra}>{extra}</div>
      </div>
      <div className={classes.content}>{children}</div>
    </Box>
  );
}

type FilterProps = {
  left?: JSX.Element[];
  right?: JSX.Element[];
};

export function PageFilter({ left, right }: FilterProps) {
  const { classes } = useStyles();

  return (
    <div className={classes.pageFilter}>
      <div className={classes.sectionElement}>{left}</div>
      <div className={classes.sectionElement}>{right}</div>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  pageLayout: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
    padding: theme.spacing.md,
    backgroundColor: theme.white,
  },
  formLayout: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
    backgroundColor: theme.white,
  },
  titleLayout: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  content: {
    display: "block",
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
  },
  subTitle: {
    fontSize: 14,
    margin: 0,
    padding: 0,
    color: theme.colors.gray[6],
  },
  extra: {
    display: "flex",
    gap: 15,
  },
  pageFilter: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: theme.colors.gray[0],
    padding: 8,
    borderRadius: 2,
  },
  sectionElement: {
    display: "flex",
    gap: 15,
  },
}));
